import { Tag } from '@zillow/constellation-10';

import {
  type BoostAdConfigDetailsDto,
  type GetMultifamilyFeesResponseDto,
  type GetUserPropertyDetailsResponseWebDto,
  RentalManagerListingCriteriaType,
  type RentalManagerListingRecommendationDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import stringHelper from '../../utils/stringHelper';
import {
  buildStatusWithRecs,
  countCheckedFeatures,
  createContentSectionStatus,
  getRecommendationKeys,
} from '../../utils/accordionStatusUtils';

import { NOT_APPLICABLE } from '../../../srcLegacy/shared/constants/ListingCriteriaConstants';
import { PACKAGES_WITH_META_ADS } from '../../../srcLegacy/shared/constants/SponsoredAdsConstants';
import {
  AIR_CONDITIONING_TYPE_KEYS,
  APPLIANCE_TYPE_KEYS,
  COMMUNICATION_TYPE_KEYS,
  COMMUNITY_ROOM_TYPE_KEYS,
  FLOOR_COVERING_TYPE_KEYS,
  HEATING_SOURCE_TYPE_KEYS,
  LEASE_LENGTH_KEYS,
  OUTDOOR_COMMON_AREA_TYPE_KEYS,
  PARKING_TYPE_KEYS,
  PROPERTY_OTHER_TYPE_KEYS,
  SECURITY_TYPE_KEYS,
  SPORT_COURT_TYPE_KEYS,
  UNIT_OTHER_TYPE_KEYS,
  UTILITIES_INCLUDED_KEYS,
  VIEW_TYPE_KEYS,
} from '../../constants/KeyFeaturesConstants';
import { RECS_TO_MF_SECTION_MAP, REVIEW_PAGE_SECTIONS } from '../../constants/ListingDetailConstants';

import type { RecommendationsBySection } from '../../hooks/queries/properties';
import type { ContentSectionStatus } from '../../utils/accordionStatusUtils';

const MF_SECTION_TO_INDEX_MAP: Record<string, number> = {
  [REVIEW_PAGE_SECTIONS.BUILDING_DETAILS]: 0,
  [REVIEW_PAGE_SECTIONS.CONTACT]: 1,
  [REVIEW_PAGE_SECTIONS.MEDIA]: 3,
  [REVIEW_PAGE_SECTIONS.FLOOR_PLANS]: 4,
  [REVIEW_PAGE_SECTIONS.SPECIAL_OFFERS]: 5,
  [REVIEW_PAGE_SECTIONS.COSTS_AND_FEES]: 6,
  [REVIEW_PAGE_SECTIONS.KEY_FEATURES]: 7,
  [REVIEW_PAGE_SECTIONS.OTHER_PROPERTY_FEATURES]: 9,
  [REVIEW_PAGE_SECTIONS.POLICIES_AND_TERMS]: 10,
  [REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA]: 13,
};

const RECS_TO_INDEX_MAPPING: Record<string, number> = Object.fromEntries(
  Object.entries(RECS_TO_MF_SECTION_MAP).map(([recs, section]) => [recs, MF_SECTION_TO_INDEX_MAP[section]]),
);

export const getAccordionIndexFromRecs = (recsValue: string): number | null => {
  const index = RECS_TO_INDEX_MAPPING[recsValue];

  if (index === undefined) {
    console.error('[MFAccordion] Unknown recommendation type for scroll-to:', recsValue);
    return null;
  }

  return index;
};

export const getAccordionTag = ({
  isIncomplete = false,
  notStarted = false,
  completeStatusDisplay = '',
  recsKeys = [],
  recommendationsList = [],
}: ContentSectionStatus & { recommendationsList: Array<RentalManagerListingRecommendationDto> }) => {
  switch (true) {
    case !isIncomplete && !notStarted && !completeStatusDisplay:
      return null;
    case isIncomplete:
      return (
        <Tag tone="critical" size="sm">
          Missing required info
        </Tag>
      );
    case Boolean(recsKeys && recsKeys.length > 0): {
      const firstRec = recommendationsList.find((rec) => rec.type === recsKeys[0]);
      const heading =
        // @ts-expect-error - text is a property of BaseStyledComponentDto
        typeof firstRec?.learnMore?.title.text === 'string' ? firstRec.learnMore.title.text : 'Recommendation';

      return (
        <Tag tone="blue" size="sm">
          {heading}
          {recsKeys.length > 1 && `, +${recsKeys.length - 1} more`}
        </Tag>
      );
    }
    case notStarted:
      return (
        <Tag tone="gray" size="sm">
          Not started
        </Tag>
      );
    case Boolean(completeStatusDisplay):
      return (
        <Tag tone="blue" size="sm">
          {completeStatusDisplay}
        </Tag>
      );
    default:
      return null;
  }
};

export const getBuildingDetailsContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const {
    name,
    numUnits,
    leaseTerms,
    website,
    fullDescription,
    logo: { url: logoUrl } = {},
    contactHours,
    isFeed,
    active,
  } = listingDetails;

  if (!isFeed && !active && (!name || !fullDescription)) {
    return createContentSectionStatus({ isIncomplete: true });
  }

  const recsKeys = getRecommendationKeys(recommendations, REVIEW_PAGE_SECTIONS.BUILDING_DETAILS);

  const dataToIterate = [
    name,
    numUnits,
    leaseTerms,
    website,
    fullDescription,
    logoUrl,
    contactHours && Object.values(contactHours).some((hour) => hour),
  ];
  const numComplete = dataToIterate.filter((value) => value).length;

  return createContentSectionStatus({
    recsKeys,
    completeStatusDisplay: `${isFeed ? numComplete : `${numComplete} / ${dataToIterate.length}`} complete`,
  });
};

export const getContactContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { additionalEmail, email, contactName, phone, forRentBy, isFeed, active, deliveryMethod } = listingDetails;

  if (!isFeed && !active && (!email || !contactName || !phone || !forRentBy)) {
    return createContentSectionStatus({ isIncomplete: true });
  }

  const dataToIterate = [email, contactName, phone, forRentBy];
  if (deliveryMethod?.isDeliveredByEmail) {
    dataToIterate.unshift(additionalEmail);
  }

  const numComplete = dataToIterate.filter((value) => value).length;
  const recsKeys = getRecommendationKeys(recommendations, REVIEW_PAGE_SECTIONS.CONTACT);

  return createContentSectionStatus({
    recsKeys,
    completeStatusDisplay: `${isFeed ? numComplete : `${numComplete} / ${dataToIterate.length}`} complete`,
  });
};

export const getBuildingMediaContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { propertyPhotos, externalTours, vrModels, isFeed, active } = listingDetails;

  if (!isFeed && !active && propertyPhotos.length === 0) {
    return createContentSectionStatus({ isIncomplete: true });
  }

  const photosDisplayText = stringHelper.pluralize(propertyPhotos.length, 'photo', 'photos');
  const totalTours = externalTours.length + vrModels.length;
  const toursDisplayText = stringHelper.pluralize(totalTours, 'tour', 'tours');

  const recsKeys = getRecommendationKeys(recommendations, REVIEW_PAGE_SECTIONS.MEDIA);

  return createContentSectionStatus({
    recsKeys,
    completeStatusDisplay: totalTours ? `${photosDisplayText}, ${toursDisplayText}` : photosDisplayText,
  });
};

export const getFloorPlansContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { floorPlans, isFeed, active } = listingDetails;

  if (
    // has no active floorplans
    (!floorPlans.some((fp) => fp.active) ||
      // has incomplete floorplans
      floorPlans.some(
        (fp) => !fp.name || !(fp.numBedrooms || fp.numBedrooms === 0) || !(fp.numBathrooms || fp.numBathrooms === 0),
      )) &&
    !isFeed &&
    !active
  ) {
    return createContentSectionStatus({ isIncomplete: true });
  }

  const recsKeys = getRecommendationKeys(recommendations, REVIEW_PAGE_SECTIONS.FLOOR_PLANS);

  // NOTE: all the units without a floor plan will be put into `GENERIC` floor plan
  const unitsCount = floorPlans.reduce((total, fp) => total + (fp.units?.length || 0), 0);
  const unitsWithoutFloorPlan = isFeed && unitsCount > 0 ? 1 : 0;
  const numberOfFloorPlans = floorPlans.length + unitsWithoutFloorPlan;

  return createContentSectionStatus({
    completeStatusDisplay: stringHelper.pluralize(numberOfFloorPlans, 'floor plan', 'floor plans'),
    recsKeys,
  });
};

export const getSpecialOffersContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { specialOffers, isFeed } = listingDetails;

  return buildStatusWithRecs({
    recommendations,
    section: REVIEW_PAGE_SECTIONS.SPECIAL_OFFERS,
    itemCount: specialOffers.length,
    isFeed,
    displayTextFn: (count) => stringHelper.pluralize(count, 'offer', 'offers'),
  });
};

export const getKeyFeaturesContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { laundry, buildingAttributes, isFeed } = listingDetails;

  const heatingSourceTypes = buildingAttributes?.heatingSourceTypes;
  const floorCoveringTypes = buildingAttributes?.floorCoveringTypes;
  const airConditioningTypes = buildingAttributes?.airConditioningTypes;

  const heatingCount = heatingSourceTypes ? countCheckedFeatures(HEATING_SOURCE_TYPE_KEYS, heatingSourceTypes) : 0;
  const floorCoveringCount = floorCoveringTypes
    ? countCheckedFeatures(FLOOR_COVERING_TYPE_KEYS, floorCoveringTypes)
    : 0;
  const airConditioningCount = airConditioningTypes
    ? countCheckedFeatures(AIR_CONDITIONING_TYPE_KEYS, airConditioningTypes)
    : 0;

  const totalCount = heatingCount + floorCoveringCount + airConditioningCount + (laundry ? 1 : 0);

  return buildStatusWithRecs({
    recommendations,
    section: REVIEW_PAGE_SECTIONS.KEY_FEATURES,
    itemCount: totalCount,
    isFeed,
    displayTextFn: (count) => stringHelper.pluralize(count, 'feature', 'features'),
  });
};

export const getUnitFeaturesContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { apartmentAmenities, buildingAttributes, isFeed } = listingDetails;

  const applianceTypes = buildingAttributes?.applianceTypes;
  const communicationTypes = buildingAttributes?.communicationTypes;

  const applianceCount = applianceTypes ? countCheckedFeatures(APPLIANCE_TYPE_KEYS, applianceTypes) : 0;
  const communicationCount = communicationTypes ? countCheckedFeatures(COMMUNICATION_TYPE_KEYS, communicationTypes) : 0;
  const otherCount = buildingAttributes ? countCheckedFeatures(UNIT_OTHER_TYPE_KEYS, buildingAttributes) : 0;

  const totalCount = applianceCount + communicationCount + otherCount + apartmentAmenities.length;

  if (totalCount === 0) {
    return createContentSectionStatus({ notStarted: !isFeed });
  }

  return createContentSectionStatus({
    completeStatusDisplay: `${totalCount} added`,
  });
};

export const getOtherPropertyFeaturesContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { propertyAmenities, buildingAttributes, isFeed } = listingDetails;

  const viewTypes = buildingAttributes?.viewTypes;
  const communityRoomTypes = buildingAttributes?.communityRoomTypes;
  const securityTypes = buildingAttributes?.securityTypes;
  const sportCourtTypes = buildingAttributes?.sportCourtTypes;
  const outdoorCommonAreaTypes = buildingAttributes?.outdoorCommonAreaTypes;

  const viewCount = viewTypes ? countCheckedFeatures(VIEW_TYPE_KEYS, viewTypes) : 0;
  const communityRoomCount = communityRoomTypes
    ? countCheckedFeatures(COMMUNITY_ROOM_TYPE_KEYS, communityRoomTypes)
    : 0;
  const securityCount = securityTypes ? countCheckedFeatures(SECURITY_TYPE_KEYS, securityTypes) : 0;
  const sportCourtCount = sportCourtTypes ? countCheckedFeatures(SPORT_COURT_TYPE_KEYS, sportCourtTypes) : 0;
  const outdoorCommonAreaCount = outdoorCommonAreaTypes
    ? countCheckedFeatures(OUTDOOR_COMMON_AREA_TYPE_KEYS, outdoorCommonAreaTypes)
    : 0;
  const otherCount = buildingAttributes ? countCheckedFeatures(PROPERTY_OTHER_TYPE_KEYS, buildingAttributes) : 0;

  const totalCount =
    viewCount +
    communityRoomCount +
    securityCount +
    sportCourtCount +
    outdoorCommonAreaCount +
    otherCount +
    propertyAmenities.length;

  return buildStatusWithRecs({
    recommendations,
    section: REVIEW_PAGE_SECTIONS.OTHER_PROPERTY_FEATURES,
    itemCount: totalCount,
    isFeed,
    displayTextFn: (count) => `${count} added`,
  });
};

export const getPoliciesAndTermsContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const { petsAllowed, buildingAttributes, isFeed } = listingDetails;

  const recsKeys = getRecommendationKeys(recommendations, REVIEW_PAGE_SECTIONS.POLICIES_AND_TERMS);

  if (recsKeys.length > 0) {
    return createContentSectionStatus({ notStarted: true, recsKeys });
  }

  const petPolicyDescription = buildingAttributes?.petPolicyDescription;
  const parkingDescription = buildingAttributes?.parkingDescription;
  const utilitiesIncluded = buildingAttributes?.utilitiesIncluded;
  const leaseLengths = buildingAttributes?.leaseLengths;
  const parkingTypes = buildingAttributes?.parkingTypes;

  const petsAllowedCount = petsAllowed
    ? Object.keys(petsAllowed).filter((pet) => (petsAllowed as unknown as Record<string, unknown>)[pet]).length
    : 0;
  const utilitiesCount = utilitiesIncluded ? countCheckedFeatures(UTILITIES_INCLUDED_KEYS, utilitiesIncluded) : 0;
  const leaseCount = leaseLengths ? countCheckedFeatures(LEASE_LENGTH_KEYS, leaseLengths) : 0;
  const parkingCount = parkingTypes ? countCheckedFeatures(PARKING_TYPE_KEYS, parkingTypes) : 0;

  const numComplete =
    petsAllowedCount +
    utilitiesCount +
    leaseCount +
    parkingCount +
    (petPolicyDescription ? 1 : 0) +
    (parkingDescription ? 1 : 0);

  if (numComplete === 0) {
    return createContentSectionStatus({ notStarted: !isFeed });
  }

  return createContentSectionStatus({ completeStatusDisplay: `${numComplete} added` });
};

export const getScreeningCriteriaStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendations?: RecommendationsBySection,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const listingCriteria = listingDetails.listingCriteria;
  const isFeed = listingDetails.isFeed || false;
  const recsKeys = getRecommendationKeys(recommendations, REVIEW_PAGE_SECTIONS.SCREENING_CRITERIA);

  if (recsKeys.length > 0) {
    return createContentSectionStatus({ notStarted: true, recsKeys });
  }

  const maxComplete = 3;
  const criteria = listingCriteria?.criteria || [];

  const incomeComplete = criteria[0]?.criterion && criteria[0].criterion !== NOT_APPLICABLE ? 1 : 0;
  const rentComplete = criteria[1]?.criterion && criteria[1].criterion !== NOT_APPLICABLE ? 1 : 0;
  const petComplete = criteria
    .slice(2)
    .filter(({ criterion, type }) => criterion && type !== RentalManagerListingCriteriaType.PET_POLICY_DOG_SIZE).length
    ? 1
    : 0;

  const numComplete = incomeComplete + rentComplete + (isFeed ? 0 : petComplete);

  if (numComplete === 0) {
    return createContentSectionStatus({ notStarted: !isFeed });
  }

  if (numComplete === maxComplete) {
    return createContentSectionStatus();
  }

  return createContentSectionStatus({ completeStatusDisplay: `${numComplete} added` });
};

export const getCostsAndFeesContentStatus = (
  recommendations: RecommendationsBySection | undefined,
  numberOfFees: number,
): ContentSectionStatus => {
  const recs = recommendations?.[REVIEW_PAGE_SECTIONS.COSTS_AND_FEES];
  const recsKeys = recs && Object.keys(recs);

  if (recsKeys && recsKeys.length > 0) {
    const mfFeeKeys = recsKeys.filter((key) => key === 'oneTimeFee' || key === 'monthlyFee');
    const filteredRecsKeys = mfFeeKeys.length > 0 ? mfFeeKeys : recsKeys;
    return createContentSectionStatus({ notStarted: true, recsKeys: filteredRecsKeys });
  }

  if (numberOfFees === 0) {
    return createContentSectionStatus({ notStarted: true });
  }

  return createContentSectionStatus({ completeStatusDisplay: `${numberOfFees} added` });
};

export const getSponsoredAdsContentStatus = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  boostListingData?: BoostAdConfigDetailsDto,
): ContentSectionStatus => {
  if (!listingDetails) {
    return createContentSectionStatus({ notStarted: true });
  }

  const productTypes = listingDetails.adproduct?.productTypes || [];
  const numberOfAds =
    productTypes.some((pt) => PACKAGES_WITH_META_ADS.includes(pt)) || boostListingData?.facebookReady ? 4 : 1;

  return createContentSectionStatus({ completeStatusDisplay: stringHelper.pluralize(numberOfAds, 'ad', 'ads') });
};

const calculateNumberOfFees = (costsAndFeesData?: GetMultifamilyFeesResponseDto): number => {
  if (!costsAndFeesData?.existingFees) return 0;

  return Object.values(costsAndFeesData.existingFees).reduce((total: number, categoryFees) => {
    return total + (Array.isArray(categoryFees) ? categoryFees.length : 0);
  }, 0);
};

export const calculateRequiredFieldsErrors = (
  listingDetails?: GetUserPropertyDetailsResponseWebDto,
  recommendationsBySection?: RecommendationsBySection,
  costsAndFeesData?: GetMultifamilyFeesResponseDto,
  boostListingData?: BoostAdConfigDetailsDto,
): Record<string, ContentSectionStatus> => {
  if (!listingDetails) {
    return {} as Record<string, ContentSectionStatus>;
  }

  const numberOfFees = calculateNumberOfFees(costsAndFeesData);

  return {
    buildingDetails: getBuildingDetailsContentStatus(listingDetails, recommendationsBySection),
    contact: getContactContentStatus(listingDetails, recommendationsBySection),
    buildingMedia: getBuildingMediaContentStatus(listingDetails, recommendationsBySection),
    floorPlans: getFloorPlansContentStatus(listingDetails, recommendationsBySection),
    specialOffers: getSpecialOffersContentStatus(listingDetails, recommendationsBySection),
    keyFeatures: getKeyFeaturesContentStatus(listingDetails, recommendationsBySection),
    unitFeatures: getUnitFeaturesContentStatus(listingDetails),
    otherPropertyFeatures: getOtherPropertyFeaturesContentStatus(listingDetails, recommendationsBySection),
    policiesAndTerms: getPoliciesAndTermsContentStatus(listingDetails, recommendationsBySection),
    screeningCriteria: getScreeningCriteriaStatus(listingDetails, recommendationsBySection),
    costsAndFees: getCostsAndFeesContentStatus(recommendationsBySection, numberOfFees),
    sponsoredAds: getSponsoredAdsContentStatus(listingDetails, boostListingData),
    applications: createContentSectionStatus(),
  };
};

export const getIsInputFormDisabled = (
  listingDetails: GetUserPropertyDetailsResponseWebDto | undefined,
  canUpdateListing: boolean,
): boolean => {
  if (!listingDetails) {
    return true;
  }

  const isFeed = listingDetails.isFeed || false;
  const isArchived = listingDetails.isArchived || false;
  const isUpdateListingRestricted = listingDetails.restrictions?.isUpdateListingRestricted || false;
  const isSelfServeOnboardingInProgress =
    listingDetails.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress || false;
  const isSSOBUpdateRestricted = listingDetails.selfServeOnboardingStatus?.isUpdateRestricted || false;

  return (
    isFeed ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (isSelfServeOnboardingInProgress && isSSOBUpdateRestricted)
  );
};
